import React, { useEffect, useState, useMemo, useCallback, ReactNode } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardHeader,
  CardBody,
  Input,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import Select from "react-select";

import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { showSuccessToast, showErrorToast } from "../../helpers/toast_helper";
import { createSelector } from "reselect";
import classnames from "classnames";

import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";
// import TableContainer from "./TableContainer";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import type { RootState } from "store";

import { fetchLeads, updateLeadStatus } from "slices/Lead/thunk";
import LeadContainer from "./LeadContainer";
import { toast } from "react-toastify";
import { exportToExcel } from "helpers/exportToExcel";
import RemarkModal from "Components/Common/RemarkModal";
import { fetchLeadListApi } from "services/leadService";
// import LeadDetailPage from "./LeadDetailPage";

const LeadRecords = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [activeTab, setActiveTab] = useState("1");
  const [tabStatus, setTabStatus] = useState<string>("");
  const [rowID, setRowId] = useState<string | null>("");
const [remarkModalOpen, setRemarkModalOpen] = useState(false);
const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
const [selectedRemark, setSelectedRemark] = useState<string>("");

  const selectLeadState = (state: any) => state;
  const leadDataSelector = createSelector(selectLeadState, (state) => ({
    leadData: state.Lead.leadData,
    singleLeadData: state.Lead.singleLeadData,
    loading: state.Lead.loading,
    pagination: state.Lead.pagination,
  }));

  const { leadData, singleLeadData, loading, pagination } = useSelector(leadDataSelector);

// Example usage inside the modal's submit handler
const handleSaveRemark = async (leadId: number, remark: string) => {
  await dispatch(updateLeadStatus(leadId, { status: "Closed", note: remark }));
  setRemarkModalOpen(false);
  // Optionally refetch leads here
  dispatch(fetchLeads({ page, per_page: limit, from_date: '', to_date: '', status: tabStatus, lead_id: 0 }));
};

const onEditRemark = (leadId: number, remark: string) => {
  setSelectedLeadId(leadId);
  setSelectedRemark(remark ?? "");
  setRemarkModalOpen(true);
};

  const leadList = leadData?.leads;
  const singleLead = singleLeadData?.leads?.[0];

  const [filteredLeadData, setFilteredLeadData] = useState<any>([]);

  useEffect(() => {
    setFilteredLeadData(leadList || []);
  }, [leadList]);

const handleExportLeads = useCallback(async () => {
  try {
    const totalItems = pagination?.total_items || filteredLeadData.length || 0;
    const response = await fetchLeadListApi({
      lead_id: 0,
      page: 1,
      per_page: totalItems > 0 ? totalItems : 1000,
      from_date: "",
      to_date: "",
      status: tabStatus,
    });

    const leads = response?.data?.leads || [];

    if (!leads.length) {
      showErrorToast("No data available to export!");
      return;
    }

    exportToExcel(leads, "Lead_Report");
    showSuccessToast("Lead report exported successfully!");
  } catch (error: any) {
    const message =
      error?.message || error?.response?.data?.message || "Failed to export leads";
    showErrorToast(message);
  }
}, [filteredLeadData.length, pagination?.total_items, tabStatus]);

  useEffect(() => {
    if (!rowID) return;
    const payload = {
      lead_id: rowID,
      page: page,
      per_page: limit,
      from_date: "",
      to_date: "",
      status: "",
    };
    dispatch(fetchLeads(payload));
  }, [rowID, page, limit, dispatch]);

  useEffect(() => {
    setPage(1);
  }, [tabStatus]);

  useEffect(() => {
    const handler = setTimeout(() => {
      dispatch(
        fetchLeads({
          lead_id: 0,
          page: page,
          per_page: limit,
          from_date: "",
          to_date: "",
          status: tabStatus,
        })
      );
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery, page, limit, tabStatus, dispatch]);

  useEffect(() => {
  }, []);

  const handlePageChange = (newPage: number) => setPage(newPage);

  const handleLeadClick = useCallback((id: string) => {
    setRowId(id);
  }, []);

  const handleBackToList = useCallback(() => {
    setRowId(null);
  }, []);

  const toUtcISOStringRange = (date: Date, isEnd: boolean = false): string => {
    const offset = date.getTimezoneOffset();
    const utcDate = new Date(date.getTime() - offset * 60000);
    utcDate.setHours(isEnd ? 23 : 0, isEnd ? 59 : 0, isEnd ? 59 : 0, isEnd ? 999 : 0);
    return utcDate.toISOString();
  };

  const highlightMatch = (text?: string | JSX.Element, query?: string) => {
    if (!query) return text;
    if (!text) return null;

    const extractText = (element: JSX.Element | string | ReactNode): string => {
      if (typeof element === "string") return element;
      if (React.isValidElement(element)) {
        if (element.props["data-no-highlight"]) return "";
        return React.Children.toArray(element.props.children)
          .map((child) => extractText(child))
          .join(" ");
      }
      return "";
    };

    const extractedText = extractText(text);

    if (!extractedText) return text;

    const regex = new RegExp(`(${query})`, "gi");
    const parts = extractedText.split(regex);

    return (
      <>
        {parts.map((part, i) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <span key={i} style={{ fontWeight: 600 }}>
              {part}
            </span>
          ) : (
            part
          )
        )}
      </>
    );
  };

  const handleValidDate = (date: any) => moment(new Date(date)).format("DD MMM Y");

  const handleValidTime = (time: any) => {
    const time1 = new Date(time);
    const getHour = time1.getHours();
    const getMin = time1.getMinutes();
    const getTime = `${getHour}:${getMin}`;
    var meridiem = getHour >= 12 ? "PM" : "AM";
    return moment(getTime, "hh:mm").format("hh:mm") + " " + meridiem;
  };

  const statusOptions = [
    { value: "Open", label: "Open" },
    { value: "Closed", label: "Close" },
    { value: "In Progress", label: "In Progress" },
  ];

  const handleStatusChange = async (leadId: number, newStatus: string, currentNote: string = "") => {
    try {
      await dispatch(updateLeadStatus(leadId, { status: newStatus, note: currentNote }));
      // Refetch leads after status update
      dispatch(fetchLeads({ page, per_page: limit, from_date: '', to_date: '', status: tabStatus, lead_id: 0 }));
      showSuccessToast("Status updated successfully!");
    } catch (error: any) {
      showErrorToast(error?.message || "Failed to update status");
    }
  };

  const getStatusColor = (status: string) => {
    const lowerVal = status?.toString().toLowerCase() || "";
    if (lowerVal === "open") return "#5374C0";
    if (lowerVal === "closed") return "#A65F00";
    if (lowerVal === "in progress") return "#28a745";
    return "#5374C0"; // default color
  };

  const columns = useMemo(() => {
    const baseColumns = [
      // {
      //   header: "S. No.",
      //   accessorKey: "id",
      //   enableColumnFilter: false,
      //   cell: (cell: any) => (page - 1) * limit + cell.row.index + 1,
      // },
      {
        header: "Lead ID",
        accessorKey: "leadno",
        enableColumnFilter: false,
        // cell: (cell: any) => {
        //   const rowNumber = cell.row.index + 1;
        //   const paddedId = rowNumber.toString().padStart(3, '0');
        //   const year = new Date().getFullYear();
        //   return (
        //     <strong>{`#LD${paddedId}`}</strong>
        //   );
        // },
      },
      {
        header: "Name",
        accessorKey: "contact_name",
        enableColumnFilter: false,
        cell: (cell: any) => highlightMatch(cell.getValue() || "N/A", searchQuery),
      },
      
       {
        header: "Service Name",
        accessorKey: "service_name",
        enableColumnFilter: false,
        cell: (cell: any) => highlightMatch(cell.getValue() || "N/A", searchQuery),
      },
      {
        header: "Product Name",
        accessorKey: "product_name",
        enableColumnFilter: false,
        cell: (cell: any) => highlightMatch(cell.getValue() || "N/A", searchQuery),
      },
  
      {
        header: "Email",
        accessorKey: "email_id",
        enableColumnFilter: false,
        cell: (cell: any) => highlightMatch(cell.getValue() || "N/A", searchQuery),
      },
      {
        header: "Mobile Number",
        accessorKey: "phone_no",
        enableColumnFilter: false,
        cell: (cell: any) => highlightMatch(cell.getValue() || "N/A", searchQuery),
      },
      {
        header: () => (
          <div style={{ textAlign: "center", whiteSpace: "nowrap", width: "100%" }}>
            Status
          </div>
        ),
        accessorKey: "status",
        enableColumnFilter: false,
        meta: {
          align: "center",
        },
        cell: (cell: any) => {
          const row = cell.row.original;
          const currentStatus = cell.getValue() || "";
          const leadId = row.id;
          const currentNote = row.note || "";
          
          // Map current status to dropdown value
          const getStatusValue = (status: string) => {
            const lowerVal = status?.toString().toLowerCase() || "";
            if (lowerVal === "open" || lowerVal === "new") return "Open";
            if (lowerVal === "closed") return "Closed";
            if (lowerVal === "in progress" || lowerVal === "picked") return "In Progress";
            return "Open"; // default
          };

          const selectedValue = getStatusValue(currentStatus);
          const statusColor = getStatusColor(selectedValue);

          return (
            <div style={{ minWidth: "150px", display: "flex", justifyContent: "center", alignItems: "center", width: "100%", textAlign: "center" }}>
              <Select
                value={statusOptions.find(opt => opt.value === selectedValue) || statusOptions[0]}
                onChange={(selected: any) => {
                  if (selected?.value && selected.value !== selectedValue) {
                    handleStatusChange(leadId, selected.value, currentNote);
                  }
                }}
                options={statusOptions}
                isSearchable={false}
                styles={{
                  control: (base: any, state: any) => ({
                    ...base,
                    border: `1px solid ${statusColor}`,
                    borderRadius: "20px",
                    minHeight: "26px",
                    height: "26px",
                    fontSize: "12px",
                    boxShadow: state.isFocused ? `0 0 0 1px ${statusColor}` : "none",
                    textAlign: "center",
                    backgroundColor: "var(--vz-body-bg, var(--bs-body-bg))",
                    "&:hover": {
                      border: `1px solid ${statusColor}`,
                    },
                  }),
                  singleValue: (base: any) => ({
                    ...base,
                    color: statusColor,
                    fontWeight: 500,
                    textAlign: "center",
                    width: "100%",
                  }),
                  valueContainer: (base: any) => ({
                    ...base,
                    justifyContent: "center",
                    textAlign: "center",
                  }),
                  indicatorSeparator: () => ({ display: "none" }),
                  dropdownIndicator: (base: any) => ({
                    ...base,
                    padding: "2px 4px",
                    color: "var(--vz-body-color, var(--bs-body-color))",
                  }),
                  menu: (base: any) => ({
                    ...base,
                    fontSize: "12px",
                    backgroundColor: "var(--vz-body-bg, var(--bs-body-bg))",
                    border: "1px solid var(--vz-border-color, var(--bs-border-color))",
                  }),
                  menuList: (base: any) => ({
                    ...base,
                    backgroundColor: "var(--vz-body-bg, var(--bs-body-bg))",
                  }),
                  option: (base: any, state: any) => ({
                    ...base,
                    backgroundColor: state.isFocused ? "var(--vz-primary, var(--bs-primary))" : "var(--vz-body-bg, var(--bs-body-bg))",
                    color: state.isFocused ? "#fff" : "var(--vz-body-color, var(--bs-body-color))",
                  }),
                }}
              />
            </div>
          );
        },
      },
      {
        header: "Date & Time",
        accessorKey: "createdon",
        enableColumnFilter: false,
        cell: (cell: any) => {
        if (!cell.getValue()) return <>N/A</>;
       return (
      <span style={{ whiteSpace: 'pre-line' }}>
        {handleValidDate(cell.getValue())},{"\n"}
        {handleValidTime(cell.getValue())}
      </span>
    );
      },
    },  
     {
  header: "Remark",
  accessorKey: "note",
  enableColumnFilter: false,
  cell: (cell: any) => {
    const leadId = cell.row.original.id;
    const remark = cell.getValue() || "";

    return (
      <div className="d-flex align-items-center justify-content-between">
        <span
          style={{
            maxWidth: "160px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={remark} // shows full text on hover
        >
          {remark || "N/A"}
        </span>

        <i
          className="ri-edit-2-line text-primary cursor-pointer ms-2"
          style={{ fontSize: "1.1rem" }}
          onClick={() => onEditRemark(leadId, remark)}
          title="Edit Remark"
        ></i>
      </div>
    );
  },
},

    ];

    const commonColumns: any[] = [];

    return [...baseColumns, ...commonColumns];
  }, [activeTab, tabStatus, page, limit, searchQuery]);

  document.title = "Leads Admin Report";

  // const toggleTab = (tab: any, type: "all" | "Open" | "Picked" | "Closed") => {
  //   if (activeTab !== tab) {
  //     setActiveTab(tab);

  //     if (type === "Open") setTabStatus("Open");
  //     else if (type === "Picked") setTabStatus("Picked");
  //     else if (type === "Closed") setTabStatus("Closed");
  //     else if (type === "all") setTabStatus("");

  //     setPage(1);
  //     setFilteredLeadData(leadList || []);
  //   }
  // };

//   if (rowID) {
//     return (
//       <div className="page-content">
//         <Container fluid>
//           <LeadDetailPage id={rowID} singleLeadData={singleLead} onBack={handleBackToList} loading={loading} />
//         </Container>
//       </div>
//     );
//   }

  return (
    <>
      <div className="page-content">
        <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
          <BreadCrumb title="Leads" pageTitle={["Report", "Lead"]} />

          <Row className="align-items-center g-3 d-flex justify-content-between pb-4 pe-3 ">
            <Col md={3} >
              <div className="position-relative">
                <Input
                  type="text"
                  className="form-control border ps-5 ms-2 rounded-3"
                  value={searchQuery}
                  placeholder="Filters"
                  disabled
                />
                <i className="ri-filter-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3" style={{ fontSize: "1.1rem" }}></i>
              </div>
            </Col>
            <Col md="auto" className="d-flex align-items-center gap-3">
              <div className="position-relative">
                <Input
                  type="text"
                  className="form-control border ps-5 ms-2 rounded-3"
                  value={searchQuery}
                  placeholder="Search leads..."
                  disabled
                />
                <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3" style={{ fontSize: "1.1rem" }}></i>
              </div>
            <button
              className="btn btn-success ms-2"
              onClick={handleExportLeads}
            >
              <i className="ri-file-excel-2-line me-1"></i>
              Export to Excel
            </button>
          </Col>
          </Row>

          <Row>
            <Col lg={12}>
              <Card id="leadsList" className="border-0 shadow-none" style={{ border: "none" }}>
                <CardHeader className="border">
                  <Row className="g-4 align-items-center">
                    <Col>
                      <Nav className="nav-tabs-custom card-header-tabs border-bottom-0" role="tablist">
                        <NavItem>
                          {/* <NavLink
                            className={classnames({ active: activeTab === "1" }, "fw-semibold")}
                            onClick={() => toggleTab("1", "all")}
                            href="#"
                          >
                            All
                          </NavLink> */}
                        </NavItem>
                        <NavItem>
                          {/* <NavLink
                            className={classnames({ active: activeTab === "2" }, "fw-semibold")}
                            onClick={() => toggleTab("2", "Open")}
                            href="#"
                          >
                            Open
                          </NavLink> */}
                        </NavItem>
                       
                        <NavItem>
                          {/* <NavLink
                            className={classnames({ active: activeTab === "4" }, "fw-semibold")}
                            onClick={() => toggleTab("4", "Closed")}
                            href="#"
                          >
                            Closed
                          </NavLink> */}
                        </NavItem>
                      </Nav>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody className="pt-3" style={{ minHeight: "500px" }}>
                  <div className="tab-content">
                    {!loading ? (
                      filteredLeadData?.length > 0 ? (
                       <LeadContainer
                          columns={columns} // Array of column configs
                          data={filteredLeadData || []} // Array of row objects
                          pagination={pagination} // Make sure this matches expected shape
                          setLimit={setLimit}
                          setPage={setPage}
                          limit={limit}
                          onPageChange={handlePageChange}
                          onEditRemark={onEditRemark}
                          // Add these lines:
                          divClass="table-responsive table-card"
                          tableClass="align-middle border-bottom rounded-pill"
                          theadClass="bg-primary text-white border-top border-success"
                          setRowId={setRowId}

  // customPageSize={limit}
  // divClass="table-responsive table-card"
  // tableClass="align-middle border-bottom rounded-pill"
  // theadClass="bg-primary text-white"
  // setRowId={setRowId}
  // ...any other optional props
/>

                      ) : (
                        <div className="text-center p-4">No records found.</div>
                      )
                    ) : (
                      <Loader />
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
        <RemarkModal
          isOpen={remarkModalOpen}
          toggle={() => setRemarkModalOpen(!remarkModalOpen)}
          leadId={selectedLeadId}          // The actual lead ID
          currentRemark={selectedRemark}   // The current remark, if any
          onSave={handleSaveRemark}
          // Optionally: pass a callback to refetch/refresh leads after update
        />

      </div>
    </>
  );
};

export default LeadRecords;
